import React, { useEffect, useState, useRef } from 'react';
import { XIcon, SearchIcon, FilterIcon, ChevronDownIcon, CheckIcon, TrendingUpIcon, ClockIcon, ChevronRightIcon } from 'lucide-react';
export interface FilterOptions {
  categories: string[];
  platforms: string[];
  priceRanges: string[];
  licenseTypes: string[];
  useCases: string[];
  formats: string[];
  skillLevels: string[];
  industries: string[];
  creatorTypes: string[];
  subcategories: {
    [category: string]: string[];
  };
}
export interface ActiveFilters {
  categories: string[];
  platforms: string[];
  priceRanges: string[];
  licenseTypes: string[];
  useCases: string[];
  formats: string[];
  skillLevels: string[];
  industries: string[];
  creatorTypes: string[];
  search: string;
  trending: boolean;
  newArrivals: boolean;
  subcategories: string[];
}
interface ProductFiltersProps {
  filterOptions: FilterOptions;
  activeFilters: ActiveFilters;
  onFilterChange: (newFilters: ActiveFilters) => void;
  totalProducts: number;
  filteredCount: number;
}
export const ProductFilters: React.FC<ProductFiltersProps> = ({
  filterOptions,
  activeFilters,
  onFilterChange,
  totalProducts,
  filteredCount
}) => {
  const [search, setSearch] = useState(activeFilters.search || '');
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    categories: true,
    platforms: true,
    priceRanges: true,
    licenseTypes: true,
    useCases: false,
    formats: false,
    skillLevels: false,
    industries: false,
    creatorTypes: false
  });
  const [expandedSubcategories, setExpandedSubcategories] = useState<{
    [category: string]: boolean;
  }>({});
  const activeSearchRef = useRef(activeFilters.search || '');
  const activeFiltersRef = useRef(activeFilters);
  const onFilterChangeRef = useRef(onFilterChange);
  // Keep refs in sync
  useEffect(() => {
    activeFiltersRef.current = activeFilters;
  }, [activeFilters]);
  useEffect(() => {
    onFilterChangeRef.current = onFilterChange;
  }, [onFilterChange]);
  // Sync search input when activeFilters.search changes externally
  useEffect(() => {
    if (activeFilters.search !== activeSearchRef.current) {
      activeSearchRef.current = activeFilters.search || '';
      setSearch(activeFilters.search || '');
    }
  }, [activeFilters.search]);
  // Debounce search — only depends on `search` to avoid re-running on every filter change
  useEffect(() => {
    const timerId = setTimeout(() => {
      if (search !== activeSearchRef.current) {
        activeSearchRef.current = search;
        onFilterChangeRef.current({
          ...activeFiltersRef.current,
          search
        });
      }
    }, 500);
    return () => clearTimeout(timerId);
  }, [search]);
  const toggleFilterSection = (section: keyof typeof expandedSections) => {
    setExpandedSections({
      ...expandedSections,
      [section]: !expandedSections[section]
    });
  };
  const toggleSubcategory = (category: string) => {
    setExpandedSubcategories({
      ...expandedSubcategories,
      [category]: !expandedSubcategories[category]
    });
  };
  const toggleFilter = (type: keyof Omit<ActiveFilters, 'search' | 'trending' | 'newArrivals' | 'subcategories'>, value: string) => {
    const currentValues = [...activeFilters[type]];
    const index = currentValues.indexOf(value);
    if (index === -1) {
      currentValues.push(value);
    } else {
      currentValues.splice(index, 1);
    }
    if (type === 'categories' && index !== -1 && filterOptions.subcategories?.[value]?.length > 0) {
      onFilterChange({
        ...activeFilters,
        [type]: currentValues,
        subcategories: activeFilters.subcategories.filter(sub => !filterOptions.subcategories?.[value]?.includes(sub))
      });
    } else {
      onFilterChange({
        ...activeFilters,
        [type]: currentValues
      });
    }
  };
  const toggleSubcategoryFilter = (subcategory: string) => {
    // Ensure subcategories is always an array
    const currentValues = [...(activeFilters.subcategories || [])];
    const index = currentValues.indexOf(subcategory);
    if (index === -1) {
      currentValues.push(subcategory);
    } else {
      currentValues.splice(index, 1);
    }
    onFilterChange({
      ...activeFilters,
      subcategories: currentValues
    });
  };
  const toggleBoolean = (field: 'trending' | 'newArrivals') => {
    onFilterChange({
      ...activeFilters,
      [field]: !activeFilters[field]
    });
  };
  const clearAllFilters = () => {
    onFilterChange({
      categories: [],
      platforms: [],
      priceRanges: [],
      licenseTypes: [],
      useCases: [],
      formats: [],
      skillLevels: [],
      industries: [],
      creatorTypes: [],
      search: '',
      trending: false,
      newArrivals: false,
      subcategories: []
    });
    setSearch('');
  };
  const hasActiveFilters = () => {
    return activeFilters.categories.length > 0 || activeFilters.platforms.length > 0 || activeFilters.priceRanges.length > 0 || activeFilters.licenseTypes.length > 0 || activeFilters.useCases.length > 0 || activeFilters.formats.length > 0 || activeFilters.skillLevels.length > 0 || activeFilters.industries.length > 0 || activeFilters.creatorTypes.length > 0 || activeFilters.subcategories.length > 0 || activeFilters.trending || activeFilters.newArrivals || activeFilters.search !== '';
  };
  const FilterSection = ({
    title,
    options,
    activeOptions,
    type
  }: {
    title: string;
    options: string[];
    activeOptions: string[];
    type: keyof Omit<ActiveFilters, 'search' | 'trending' | 'newArrivals' | 'subcategories'>;
  }) => {
    const isExpanded = expandedSections[type as keyof typeof expandedSections];
    return <div className="mb-6" data-id="element-1411">
        <button onClick={() => toggleFilterSection(type as keyof typeof expandedSections)} className="flex items-center justify-between w-full text-left font-medium text-gray-800 dark:text-[#F8FAF9] mb-2" data-id="element-1412">
          <span data-id="element-1413">{title}</span>
          <ChevronDownIcon className={`h-5 w-5 text-gray-500 dark:text-[#7A8F8A] transition-transform ${isExpanded ? 'transform rotate-180' : ''}`} data-id="element-1414" />
        </button>
        {isExpanded && <div className="space-y-2 mt-2" data-id="element-1415">
            {options.map(option => <div key={option} className="flex flex-col" data-id="element-1416">
                <div className="flex items-center" data-id="element-1417">
                  <button onClick={() => toggleFilter(type, option)} className="group flex items-center" data-id="element-1418">
                    <div className={`w-5 h-5 rounded flex items-center justify-center mr-3 border transition-colors ${activeOptions.includes(option) ? 'bg-[#2DB489] dark:bg-[#4ED1A0] border-[#2DB489] dark:border-[#4ED1A0]' : 'border-gray-300 dark:border-[#1A3330] group-hover:border-[#2DB489] dark:group-hover:border-[#4ED1A0]'}`} data-id="element-1419">
                      {activeOptions.includes(option) && <CheckIcon className="h-3 w-3 text-white" data-id="element-1420" />}
                    </div>
                    <span className="text-sm text-gray-700 dark:text-[#B0C1BD]" data-id="element-1421">
                      {option}
                    </span>
                  </button>
                  {type === 'categories' && filterOptions.subcategories[option] && <button onClick={() => toggleSubcategory(option)} className="ml-auto p-1 text-gray-500 dark:text-[#7A8F8A] hover:text-gray-700 dark:hover:text-[#B0C1BD]" data-id="element-1422">
                        <ChevronRightIcon className={`h-4 w-4 transition-transform ${expandedSubcategories[option] ? 'transform rotate-90' : ''}`} data-id="element-1423" />
                      </button>}
                </div>
                {type === 'categories' && filterOptions.subcategories?.[option] && expandedSubcategories[option] && <div className="ml-8 mt-2 space-y-2" data-id="element-1424">
                      {filterOptions.subcategories[option].map(subcategory => <div key={subcategory} className="flex items-center" data-id="element-1425">
                            <button onClick={() => toggleSubcategoryFilter(subcategory)} className="group flex items-center" data-id="element-1426">
                              <div className={`w-4 h-4 rounded flex items-center justify-center mr-2 border transition-colors ${(activeFilters.subcategories || []).includes(subcategory) ? 'bg-[#2DB489] dark:bg-[#4ED1A0] border-[#2DB489] dark:border-[#4ED1A0]' : 'border-gray-300 dark:border-[#1A3330] group-hover:border-[#2DB489] dark:group-hover:border-[#4ED1A0]'}`} data-id="element-1427">
                                {(activeFilters.subcategories || []).includes(subcategory) && <CheckIcon className="h-2.5 w-2.5 text-white" data-id="element-1428" />}
                              </div>
                              <span className="text-sm text-gray-600 dark:text-[#A0B1AD]" data-id="element-1429">
                                {subcategory}
                              </span>
                            </button>
                          </div>)}
                    </div>}
              </div>)}
          </div>}
      </div>;
  };
  return <div className="w-full" data-id="element-1430">
      <div className="flex flex-col md:flex-row items-stretch md:items-center gap-4 mb-6" data-id="element-1431">
        <div className="relative flex-grow" data-id="element-1432">
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search products..." className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 dark:border-[#1A3330] focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] dark:bg-[#0F1F1D] dark:text-[#B0C1BD]" data-id="element-1433" />
          <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 dark:text-[#7A8F8A]" data-id="element-1434" />
          {search && <button onClick={() => {
          setSearch('');
          onFilterChange({
            ...activeFilters,
            search: ''
          });
        }} className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 dark:text-[#7A8F8A] hover:text-gray-600 dark:hover:text-[#B0C1BD]" data-id="element-1435">
              <XIcon className="h-4 w-4" data-id="element-1436" />
            </button>}
        </div>
        <button onClick={() => setMobileFiltersOpen(!mobileFiltersOpen)} className="md:hidden flex items-center justify-center px-4 py-2 bg-white dark:bg-[#162825] border border-gray-300 dark:border-[#1A3330] rounded-lg" data-id="element-1437">
          <FilterIcon className="h-5 w-5 mr-2 text-gray-500 dark:text-[#7A8F8A]" data-id="element-1438" />
          <span className="text-gray-700 dark:text-[#B0C1BD]" data-id="element-1439">Filters</span>
        </button>
      </div>

      {hasActiveFilters() && <div className="flex flex-wrap items-center gap-2 mb-6" style={{
      minHeight: '2rem'
    }} data-id="element-1440">
          {activeFilters.search && <div className="flex items-center bg-gray-100 dark:bg-[#1A3330] text-gray-800 dark:text-[#B0C1BD] px-3 py-1 rounded-full text-sm" data-id="element-1441">
              <span data-id="element-1442">Search: {activeFilters.search}</span>
              <button onClick={() => {
          setSearch('');
          onFilterChange({
            ...activeFilters,
            search: ''
          });
        }} className="ml-2 text-gray-500 dark:text-[#7A8F8A] hover:text-gray-700 dark:hover:text-[#B0C1BD]" data-id="element-1443">
                <XIcon className="h-3 w-3" data-id="element-1444" />
              </button>
            </div>}
          {activeFilters.trending && <div className="flex items-center bg-gray-100 dark:bg-[#1A3330] text-gray-800 dark:text-[#B0C1BD] px-3 py-1 rounded-full text-sm" data-id="element-1445">
              <span data-id="element-1446">Top Trending</span>
              <button onClick={() => toggleBoolean('trending')} className="ml-2 text-gray-500 dark:text-[#7A8F8A] hover:text-gray-700 dark:hover:text-[#B0C1BD]" data-id="element-1447">
                <XIcon className="h-3 w-3" data-id="element-1448" />
              </button>
            </div>}
          {activeFilters.newArrivals && <div className="flex items-center bg-gray-100 dark:bg-[#1A3330] text-gray-800 dark:text-[#B0C1BD] px-3 py-1 rounded-full text-sm" data-id="element-1449">
              <span data-id="element-1450">New Arrivals</span>
              <button onClick={() => toggleBoolean('newArrivals')} className="ml-2 text-gray-500 dark:text-[#7A8F8A] hover:text-gray-700 dark:hover:text-[#B0C1BD]" data-id="element-1451">
                <XIcon className="h-3 w-3" data-id="element-1452" />
              </button>
            </div>}
          {Object.entries(activeFilters).map(([type, values]) => {
        if (type === 'search' || type === 'trending' || type === 'newArrivals' || type === 'subcategories' || values.length === 0) return null;
        return values.map(value => <div key={`${type}-${value}`} className="flex items-center bg-gray-100 dark:bg-[#1A3330] text-gray-800 dark:text-[#B0C1BD] px-3 py-1 rounded-full text-sm" data-id="element-1453">
                <span data-id="element-1454">{value}</span>
                <button onClick={() => toggleFilter(type as keyof Omit<ActiveFilters, 'search' | 'trending' | 'newArrivals' | 'subcategories'>, value)} className="ml-2 text-gray-500 dark:text-[#7A8F8A] hover:text-gray-700 dark:hover:text-[#B0C1BD]" data-id="element-1455">
                  <XIcon className="h-3 w-3" data-id="element-1456" />
                </button>
              </div>);
      })}
          {activeFilters.subcategories.map(subcategory => <div key={`subcategory-${subcategory}`} className="flex items-center bg-gray-100 dark:bg-[#1A3330] text-gray-800 dark:text-[#B0C1BD] px-3 py-1 rounded-full text-sm" data-id="element-1457">
              <span data-id="element-1458">{subcategory}</span>
              <button onClick={() => toggleSubcategoryFilter(subcategory)} className="ml-2 text-gray-500 dark:text-[#7A8F8A] hover:text-gray-700 dark:hover:text-[#B0C1BD]" data-id="element-1459">
                <XIcon className="h-3 w-3" data-id="element-1460" />
              </button>
            </div>)}
          <button onClick={clearAllFilters} className="flex items-center justify-center px-3 py-1 bg-gray-100 dark:bg-[#1A3330] hover:bg-gray-200 dark:hover:bg-[#0B2C2A] rounded-full transition-colors" data-id="element-1461">
            <XIcon className="h-3 w-3 mr-1.5 text-gray-600 dark:text-[#B0C1BD]" data-id="element-1462" />
            <span className="text-gray-700 dark:text-[#B0C1BD] text-sm" data-id="element-1463">
              Clear all filters
            </span>
          </button>
        </div>}

      <div className="flex justify-between items-center mb-6" style={{
      minHeight: '1.5rem'
    }} data-id="element-1464">
        <p className="text-sm text-gray-600 dark:text-[#7A8F8A]" data-id="element-1465">
          Showing{' '}
          <span className="font-medium text-gray-800 dark:text-[#B0C1BD]" data-id="element-1466">
            {filteredCount}
          </span>{' '}
          of {totalProducts} products
        </p>
      </div>

      <div className="hidden md:block w-full" data-id="element-1467">
        <div className="bg-white dark:bg-[#162825] rounded-xl p-6 border border-gray-100 dark:border-[#1A3330] sticky top-24" data-id="element-1468">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-[#F8FAF9] mb-6" data-id="element-1469">
            Filters
          </h3>
          <div className="mb-6" data-id="element-1470">
            <div className="flex items-center justify-between mb-3" data-id="element-1471">
              <div className="flex items-center" data-id="element-1472">
                <button onClick={() => toggleBoolean('trending')} className="group flex items-center" data-id="element-1473">
                  <div className={`w-5 h-5 rounded flex items-center justify-center mr-3 border transition-colors ${activeFilters.trending ? 'bg-[#2DB489] dark:bg-[#4ED1A0] border-[#2DB489] dark:border-[#4ED1A0]' : 'border-gray-300 dark:border-[#1A3330] group-hover:border-[#2DB489] dark:group-hover:border-[#4ED1A0]'}`} data-id="element-1474">
                    {activeFilters.trending && <CheckIcon className="h-3 w-3 text-white" data-id="element-1475" />}
                  </div>
                  <span className="text-sm font-medium text-gray-700 dark:text-[#B0C1BD] flex items-center" data-id="element-1476">
                    <TrendingUpIcon className="h-4 w-4 mr-1.5" data-id="element-1477" />
                    Top Trending
                  </span>
                </button>
              </div>
            </div>
            <div className="flex items-center justify-between" data-id="element-1478">
              <div className="flex items-center" data-id="element-1479">
                <button onClick={() => toggleBoolean('newArrivals')} className="group flex items-center" data-id="element-1480">
                  <div className={`w-5 h-5 rounded flex items-center justify-center mr-3 border transition-colors ${activeFilters.newArrivals ? 'bg-[#2DB489] dark:bg-[#4ED1A0] border-[#2DB489] dark:border-[#4ED1A0]' : 'border-gray-300 dark:border-[#1A3330] group-hover:border-[#2DB489] dark:group-hover:border-[#4ED1A0]'}`} data-id="element-1481">
                    {activeFilters.newArrivals && <CheckIcon className="h-3 w-3 text-white" data-id="element-1482" />}
                  </div>
                  <span className="text-sm font-medium text-gray-700 dark:text-[#B0C1BD] flex items-center" data-id="element-1483">
                    <ClockIcon className="h-4 w-4 mr-1.5" data-id="element-1484" />
                    New Arrivals
                  </span>
                </button>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-200 dark:border-[#1A3330] my-4" data-id="element-1485"></div>
          <FilterSection title="Category" options={filterOptions.categories} activeOptions={activeFilters.categories} type="categories" data-id="element-1486" />
          <FilterSection title="Platform" options={filterOptions.platforms} activeOptions={activeFilters.platforms} type="platforms" data-id="element-1487" />
          <FilterSection title="Format" options={filterOptions.formats} activeOptions={activeFilters.formats} type="formats" data-id="element-1488" />
          <FilterSection title="Skill Level" options={filterOptions.skillLevels} activeOptions={activeFilters.skillLevels} type="skillLevels" data-id="element-1489" />
          <FilterSection title="Industry / Niche" options={filterOptions.industries} activeOptions={activeFilters.industries} type="industries" data-id="element-1490" />
          <FilterSection title="Creator Type" options={filterOptions.creatorTypes} activeOptions={activeFilters.creatorTypes} type="creatorTypes" data-id="element-1491" />
          <FilterSection title="Price Range" options={filterOptions.priceRanges} activeOptions={activeFilters.priceRanges} type="priceRanges" data-id="element-1492" />
          <FilterSection title="License Type" options={filterOptions.licenseTypes} activeOptions={activeFilters.licenseTypes} type="licenseTypes" data-id="element-1493" />
          <FilterSection title="Use Case" options={filterOptions.useCases} activeOptions={activeFilters.useCases} type="useCases" data-id="element-1494" />
        </div>
      </div>

      {mobileFiltersOpen && <div className="md:hidden fixed inset-0 z-50 bg-black bg-opacity-50" data-id="element-1495">
          <div className="absolute bottom-0 left-0 right-0 bg-white dark:bg-[#162825] rounded-t-xl p-6 max-h-[80vh] overflow-y-auto" data-id="element-1496">
            <div className="flex justify-between items-center mb-6" data-id="element-1497">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-[#F8FAF9]" data-id="element-1498">
                Filters
              </h3>
              <button onClick={() => setMobileFiltersOpen(false)} className="text-gray-500 dark:text-[#7A8F8A]" data-id="element-1499">
                <XIcon className="h-6 w-6" data-id="element-1500" />
              </button>
            </div>
            <div className="mb-6" data-id="element-1501">
              <div className="flex items-center justify-between mb-3" data-id="element-1502">
                <div className="flex items-center" data-id="element-1503">
                  <button onClick={() => toggleBoolean('trending')} className="group flex items-center" data-id="element-1504">
                    <div className={`w-5 h-5 rounded flex items-center justify-center mr-3 border transition-colors ${activeFilters.trending ? 'bg-[#2DB489] dark:bg-[#4ED1A0] border-[#2DB489] dark:border-[#4ED1A0]' : 'border-gray-300 dark:border-[#1A3330] group-hover:border-[#2DB489] dark:group-hover:border-[#4ED1A0]'}`} data-id="element-1505">
                      {activeFilters.trending && <CheckIcon className="h-3 w-3 text-white" data-id="element-1506" />}
                    </div>
                    <span className="text-sm font-medium text-gray-700 dark:text-[#B0C1BD] flex items-center" data-id="element-1507">
                      <TrendingUpIcon className="h-4 w-4 mr-1.5" data-id="element-1508" />
                      Top Trending
                    </span>
                  </button>
                </div>
              </div>
              <div className="flex items-center justify-between" data-id="element-1509">
                <div className="flex items-center" data-id="element-1510">
                  <button onClick={() => toggleBoolean('newArrivals')} className="group flex items-center" data-id="element-1511">
                    <div className={`w-5 h-5 rounded flex items-center justify-center mr-3 border transition-colors ${activeFilters.newArrivals ? 'bg-[#2DB489] dark:bg-[#4ED1A0] border-[#2DB489] dark:border-[#4ED1A0]' : 'border-gray-300 dark:border-[#1A3330] group-hover:border-[#2DB489] dark:group-hover:border-[#4ED1A0]'}`} data-id="element-1512">
                      {activeFilters.newArrivals && <CheckIcon className="h-3 w-3 text-white" data-id="element-1513" />}
                    </div>
                    <span className="text-sm font-medium text-gray-700 dark:text-[#B0C1BD] flex items-center" data-id="element-1514">
                      <ClockIcon className="h-4 w-4 mr-1.5" data-id="element-1515" />
                      New Arrivals
                    </span>
                  </button>
                </div>
              </div>
            </div>
            <div className="border-t border-gray-200 dark:border-[#1A3330] my-4" data-id="element-1516"></div>
            <FilterSection title="Category" options={filterOptions.categories} activeOptions={activeFilters.categories} type="categories" data-id="element-1517" />
            <FilterSection title="Platform" options={filterOptions.platforms} activeOptions={activeFilters.platforms} type="platforms" data-id="element-1518" />
            <FilterSection title="Format" options={filterOptions.formats} activeOptions={activeFilters.formats} type="formats" data-id="element-1519" />
            <FilterSection title="Skill Level" options={filterOptions.skillLevels} activeOptions={activeFilters.skillLevels} type="skillLevels" data-id="element-1520" />
            <FilterSection title="Industry / Niche" options={filterOptions.industries} activeOptions={activeFilters.industries} type="industries" data-id="element-1521" />
            <FilterSection title="Creator Type" options={filterOptions.creatorTypes} activeOptions={activeFilters.creatorTypes} type="creatorTypes" data-id="element-1522" />
            <FilterSection title="Price Range" options={filterOptions.priceRanges} activeOptions={activeFilters.priceRanges} type="priceRanges" data-id="element-1523" />
            <FilterSection title="License Type" options={filterOptions.licenseTypes} activeOptions={activeFilters.licenseTypes} type="licenseTypes" data-id="element-1524" />
            <FilterSection title="Use Case" options={filterOptions.useCases} activeOptions={activeFilters.useCases} type="useCases" data-id="element-1525" />
            <div className="mt-6 flex gap-4" data-id="element-1526">
              {hasActiveFilters() && <button onClick={clearAllFilters} className="flex-1 py-3 bg-gray-100 dark:bg-[#1A3330] rounded-lg text-gray-700 dark:text-[#B0C1BD] font-medium" data-id="element-1527">
                  Clear all
                </button>}
              <button onClick={() => setMobileFiltersOpen(false)} className="flex-1 py-3 bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] text-white rounded-lg font-medium" data-id="element-1528">
                Apply filters
              </button>
            </div>
          </div>
        </div>}
    </div>;
};